/**
 * ---------------------------------------------------------------------------
 * Leadership Request ("ticket") workflow engine
 * ---------------------------------------------------------------------------
 * Deliberately mirrors `workflow.ts`'s shape — the same declarative table,
 * the same `canTransition`/`availableTransitions` authority shared by server
 * and client — because a ticket's lifecycle is a small subset of the same
 * problem a task already solves. It is not the task engine reused directly:
 * a ticket has four states and three actions, not eleven and thirteen, and
 * conflating the two would make an already-large state machine harder to
 * reason about for no shared benefit.
 */
export const TICKET_STATUSES = ['NEW', 'CLAIMED', 'COMPLETED', 'DENIED'];
export function isTicketStatus(value) {
    return typeof value === 'string' && TICKET_STATUSES.includes(value);
}
export const TICKET_STATUS_META = {
    NEW: {
        key: 'NEW',
        label: 'New',
        description: 'Visible to every developer. Not yet claimed by anyone.',
        group: 'OPEN',
        tone: 'neutral',
        icon: 'circle-dashed',
        order: 10,
        isTerminal: false,
    },
    CLAIMED: {
        key: 'CLAIMED',
        label: 'Claimed',
        description: 'Assigned to one developer by a director. Only that developer and directors can see it now.',
        group: 'OPEN',
        tone: 'info',
        icon: 'hand',
        order: 20,
        isTerminal: false,
    },
    COMPLETED: {
        key: 'COMPLETED',
        label: 'Completed',
        description: 'Signed off by a director. The requester has been notified.',
        group: 'CLOSED',
        tone: 'success',
        icon: 'badge-check',
        order: 30,
        isTerminal: true,
    },
    DENIED: {
        key: 'DENIED',
        label: 'Denied',
        description: 'Closed by a director without completion.',
        group: 'CLOSED',
        tone: 'critical',
        icon: 'circle-slash',
        order: 40,
        isTerminal: true,
    },
};
export const OPEN_TICKET_STATUSES = TICKET_STATUSES.filter((s) => TICKET_STATUS_META[s].group === 'OPEN');
export const TICKET_ACTIONS = ['ASSIGN', 'COMPLETE', 'DENY'];
export function isTicketAction(value) {
    return typeof value === 'string' && TICKET_ACTIONS.includes(value);
}
export const TICKET_TRANSITIONS = [
    {
        // Also used to REASSIGN a CLAIMED ticket to a different developer — one
        // action, since both cases are "a director decides who owns this now".
        action: 'ASSIGN',
        from: ['NEW', 'CLAIMED'],
        to: 'CLAIMED',
        label: 'Assign',
        description: 'Claim this request for one developer. Everyone else loses access to it.',
        permission: 'ticket:assign',
        requiresComment: false,
        requiresAssignee: true,
        intent: 'primary',
    },
    {
        action: 'COMPLETE',
        from: ['CLAIMED'],
        to: 'COMPLETED',
        label: 'Mark Complete',
        description: 'Sign off the request. The department lead who opened it is notified.',
        permission: 'ticket:complete',
        requiresComment: false,
        requiresAssignee: false,
        intent: 'positive',
    },
    {
        // Deliberately available from every open state, not only CLAIMED — a
        // director can deny an unclaimed request outright, with no developer
        // ever having touched it.
        action: 'DENY',
        from: ['NEW', 'CLAIMED'],
        to: 'DENIED',
        label: 'Deny & Close',
        description: 'Close this request without completing it. A reason is required.',
        permission: 'ticket:deny',
        requiresComment: true,
        requiresAssignee: false,
        intent: 'danger',
    },
];
const TICKET_TRANSITION_BY_ACTION = new Map(TICKET_TRANSITIONS.map((t) => [t.action, t]));
export function getTicketTransition(action) {
    return TICKET_TRANSITION_BY_ACTION.get(action);
}
export function ticketTransitionsFrom(status) {
    return TICKET_TRANSITIONS.filter((t) => t.from.includes(status));
}
/**
 * Single authority for "may this actor run this action on this ticket right
 * now" — the server enforces it, the web client calls the same function to
 * decide which buttons to draw (§21).
 */
export function canTicketTransition(actor, status, action, options = {}) {
    const transition = TICKET_TRANSITION_BY_ACTION.get(action);
    if (!transition)
        return { allowed: false, reason: 'UNKNOWN_ACTION' };
    if (!transition.from.includes(status)) {
        return { allowed: false, reason: 'INVALID_FROM_STATUS', transition };
    }
    if (!actor.permissions.includes(transition.permission)) {
        return { allowed: false, reason: 'NOT_PERMITTED', transition };
    }
    if (transition.requiresComment && !options.comment?.trim()) {
        return { allowed: false, reason: 'COMMENT_REQUIRED', transition };
    }
    if (transition.requiresAssignee && !options.assigneeId) {
        return { allowed: false, reason: 'ASSIGNEE_REQUIRED', transition };
    }
    return { allowed: true, transition };
}
export function availableTicketTransitions(actor, status) {
    return ticketTransitionsFrom(status).filter((t) => {
        const check = canTicketTransition(actor, status, t.action, {
            comment: t.requiresComment ? 'placeholder' : null,
            assigneeId: t.requiresAssignee ? 'placeholder' : null,
        });
        return check.allowed;
    });
}
export const TICKET_TRANSITION_DENIAL_MESSAGES = {
    UNKNOWN_ACTION: 'That action does not exist.',
    INVALID_FROM_STATUS: 'This request is not in a state where that action is valid.',
    NOT_PERMITTED: 'You do not have permission to do that.',
    COMMENT_REQUIRED: 'A reason is required for this action.',
    ASSIGNEE_REQUIRED: 'A developer must be selected.',
};
//# sourceMappingURL=ticket-workflow.js.map